import { DataVersion } from '@hakimo-ui/hakimo/types';
import { useAlarm } from '@hakimo-ui/hakimo/data-access';
import { NotFound, QueryResult } from '@hakimo-ui/hakimo/ui-elements';
import {
  appendVersionParam,
  parseDataVersionParam,
  withAuthz,
  withErrorBoundary,
} from '@hakimo-ui/hakimo/util';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ScanRawAlarmDetailsData } from '../scan-raw-alarms/ScanRawAlarmDetailsRoot';
import AlarmDetails from './AlarmDetails';

dayjs.extend(utc);

type Params = {
  alarmId: string;
};

interface Props {
  alarmId: string;
  partitionKey?: string;
  dataVersion?: DataVersion;
}

function AlarmDetailsData({ alarmId, partitionKey, dataVersion }: Props) {
  const navigate = useNavigate();
  const queryResult = useAlarm(alarmId, partitionKey, dataVersion);
  const { refetch } = queryResult;

  useEffect(() => {
    if (!queryResult.data) {
      return;
    }
    if (!partitionKey) {
      const date = dayjs.utc(queryResult.data?.timestamp);
      const calculatedPartitionKey = date.format('YYYYMM');
      navigate(
        appendVersionParam(
          `/alarms/${alarmId}?partitionKey=${calculatedPartitionKey}`,
          dataVersion
        ),
        { replace: true }
      );
    }
  }, [partitionKey, alarmId, navigate, queryResult.data, dataVersion]);

  return (
    <QueryResult queryResult={queryResult}>
      {(data) => (
        <AlarmDetails
          alarm={data}
          onRefresh={() => refetch()}
          dataVersion={dataVersion}
        />
      )}
    </QueryResult>
  );
}

function AlarmDetailsRoot() {
  const { alarmId } = useParams<Params>();
  const [searchParams] = useSearchParams();
  const partitionKey = searchParams.get('partitionKey') ?? undefined;
  const product = searchParams.get('product');
  const dataVersion = parseDataVersionParam(searchParams);

  if (alarmId === undefined) {
    return <NotFound />;
  }

  return product === 'scan' ? (
    <ScanRawAlarmDetailsData eventId={alarmId} />
  ) : (
    <AlarmDetailsData
      alarmId={alarmId}
      partitionKey={partitionKey}
      dataVersion={dataVersion}
    />
  );
}

export default withAuthz(withErrorBoundary(AlarmDetailsRoot), [
  'alarm/details:view',
]);
